import { HttpContext, HttpContextToken } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationSkipped,
  NavigationStart,
  Router,
} from '@angular/router';
import { Observable, defer, finalize } from 'rxjs';

/** A GET that polls or runs in the background: it never shows the page progress bar. */
export const BACKGROUND_REQUEST = new HttpContextToken<boolean>(() => false);

/** The context a polling or background GET passes. */
export function backgroundRequest(): HttpContext {
  return new HttpContext().set(BACKGROUND_REQUEST, true);
}

/** Fast pages never see the bar. */
export const SHOW_AFTER_MS = 150;
/** Once shown, the bar stays this long so it does not blink. */
export const MIN_VISIBLE_MS = 300;
/** The opened page starts its GETs in ngOnInit, just after the navigation ends; they still count. */
export const SETTLE_MS = 100;

/**
 * The app-wide "the page is loading" state: from a navigation's start until it
 * ends and the GETs the opened page started have answered.
 */
@Injectable({ providedIn: 'root' })
export class PageProgressService {
  readonly visible = signal(false);

  private loading = false;
  private navigating = false;
  private pending = 0;
  private delayPassed = false;
  private shownAt = 0;
  private showTimer?: ReturnType<typeof setTimeout>;
  private settleTimer?: ReturnType<typeof setTimeout>;
  private hideTimer?: ReturnType<typeof setTimeout>;

  constructor() {
    inject(Router)
      .events.pipe(takeUntilDestroyed())
      .subscribe((event) => {
        if (event instanceof NavigationStart) {
          this.navigating = true;
          clearTimeout(this.hideTimer);
          if (!this.loading) {
            this.loading = true;
            this.delayPassed = false;
            this.showTimer = setTimeout(() => {
              this.delayPassed = true;
              this.update();
            }, SHOW_AFTER_MS);
          }
          this.update();
        } else if (
          event instanceof NavigationEnd ||
          event instanceof NavigationCancel ||
          event instanceof NavigationError ||
          event instanceof NavigationSkipped
        ) {
          this.navigating = false;
          this.update();
        }
      });
  }

  /** Counts the request while the page loads; a GET started later (a poll, a click) passes through. */
  track<T>(request: Observable<T>): Observable<T> {
    return defer(() => {
      if (!this.loading) return request;
      this.pending++;
      this.update();
      return request.pipe(
        finalize(() => {
          this.pending--;
          this.update();
        })
      );
    });
  }

  private update(): void {
    if (!this.loading) return;
    const busy = this.navigating || this.pending > 0;
    clearTimeout(this.settleTimer);
    if (busy) {
      if (this.delayPassed && !this.visible()) {
        this.shownAt = Date.now();
        this.visible.set(true);
      }
    } else {
      this.settleTimer = setTimeout(() => this.end(), SETTLE_MS);
    }
  }

  private end(): void {
    this.loading = false;
    clearTimeout(this.showTimer);
    if (!this.visible()) return;
    const left = MIN_VISIBLE_MS - (Date.now() - this.shownAt);
    this.hideTimer = setTimeout(() => this.visible.set(false), Math.max(0, left));
  }
}
